import { Button, Modal, Select, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconBuildingHospital, IconShieldCheck, IconX } from '@tabler/icons-react'

export type EligibilityDecisionMode = 'approve' | 'disapprove'

type EligibilityDecisionForm = {
  reason: string
  otherReason: string
  notes: string
}

type Props = {
  mode: EligibilityDecisionMode
  opened: boolean
  onClose: () => void
  onSubmit: (values: { reason: string; notes: string }) => Promise<void>
  loading?: boolean
  context?: 'eligibility' | 'organization'
  /** Override the modal's stacking order — needed when opened from within a portaled popover. */
  zIndex?: number
}

const OTHER_REASON_VALUE = 'Other'

const MODE_CONFIG: Record<
  'eligibility' | 'organization',
  Record<
    EligibilityDecisionMode,
    {
      title: string
      submitLabel: string
      submitColor: string
      notesLabel: string
      notesPlaceholder: string
      reasons: string[]
    }
  >
> = {
  eligibility: {
    approve: {
      title: 'Approve Eligibility',
      submitLabel: 'Save Approval',
      submitColor: 'green',
      notesLabel: 'Approval Notes',
      notesPlaceholder: 'Explain how eligibility was verified or what the patient should know',
      reasons: [
        'Verified via Availity',
        'Verified in the payer portal',
        'Verified via payer website',
        'Self-pay',
        OTHER_REASON_VALUE,
      ],
    },
    disapprove: {
      title: 'Disapprove Eligibility',
      submitLabel: 'Save Disapproval',
      submitColor: 'red',
      notesLabel: 'CC Action / Patient Outreach Reason',
      notesPlaceholder: 'Explain what the care coordinator should tell the patient',
      reasons: [
        'We do not accept this insurance yet',
        'Coverage inactive',
        'Out of network',
        'Eligibility could not be confirmed',
        'Wrong insurance information',
        OTHER_REASON_VALUE,
      ],
    },
  },
  organization: {
    approve: {
      title: 'Approve Insurance For Clinic',
      submitLabel: 'Save Decision',
      submitColor: 'green',
      notesLabel: 'Decision Notes',
      notesPlaceholder: 'Explain why this clinic can work with this insurance',
      reasons: [
        'In network',
        'Clinic approved this payer',
        'Existing payer relationship',
        OTHER_REASON_VALUE,
      ],
    },
    disapprove: {
      title: 'Decline Insurance For Clinic',
      submitLabel: 'Save Decision',
      submitColor: 'red',
      notesLabel: 'Decision Notes',
      notesPlaceholder: 'Explain why this clinic cannot work with this insurance',
      reasons: [
        'We do not accept this insurance',
        'Out of network',
        'Payer contract unavailable',
        'Information needs correction',
        OTHER_REASON_VALUE,
      ],
    },
  },
}

export function EligibilityDecisionModal({
  mode,
  opened,
  onClose,
  onSubmit,
  loading,
  context = 'eligibility',
  zIndex,
}: Props) {
  const config = MODE_CONFIG[context][mode]
  const isApprove = mode === 'approve'
  const accentClasses = isApprove
    ? 'border-[#BFE3CC] bg-[#E6F6EC] text-[#1A8245]'
    : 'border-[#F3C9C0] bg-[#FDECE8] text-[#E5533D]'
  const submitClasses = isApprove
    ? 'bg-[#1A8245] hover:bg-[#146B38]'
    : 'bg-[#E5533D] hover:bg-[#C94431]'
  const eyebrow = context === 'organization' ? 'Clinic decision' : 'Eligibility review'
  const description =
    context === 'organization'
      ? 'Record how this clinic should route the attached insurance.'
      : 'Save the review outcome and care team notes.'
  const form = useForm<EligibilityDecisionForm>({
    initialValues: { reason: '', otherReason: '', notes: '' },
    validate: {
      reason: (value) => (!value ? 'Reason is required' : null),
      otherReason: (value, values) =>
        values.reason === OTHER_REASON_VALUE && !value.trim() ? 'Reason is required' : null,
      notes: (value) => (!value.trim() ? 'Notes are required' : null),
    },
  })

  const handleClose = () => {
    form.reset()
    onClose()
  }

  const handleSubmit = async (values: EligibilityDecisionForm) => {
    const finalReason =
      values.reason === OTHER_REASON_VALUE ? values.otherReason.trim() : values.reason
    await onSubmit({ reason: finalReason, notes: values.notes.trim() })
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      zIndex={zIndex}
      size={460}
      padding={0}
      radius={16}
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.24, blur: 2 }}
      styles={{
        content: {
          border: '1px solid #E4E7EB',
          boxShadow: '0 14px 36px rgba(16,24,40,.18)',
          overflow: 'hidden',
        },
        body: { padding: 0 },
      }}
    >
      <form onSubmit={form.onSubmit((values) => void handleSubmit(values))}>
        <div className="flex items-start gap-3 border-b border-[#EEF0F2] px-[18px] py-4">
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${accentClasses}`}
          >
            {context === 'organization' ? (
              <IconBuildingHospital size={18} stroke={2} />
            ) : (
              <IconShieldCheck size={18} stroke={2} />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 text-[11px] font-bold tracking-normal text-[#9AA0A8] uppercase">
              {eyebrow}
            </div>
            <div className="text-[18px] leading-6 font-extrabold tracking-tight text-[#1F2733]">
              {config.title}
            </div>
            <div className="mt-1 text-[12.5px] leading-5 font-medium text-[#6B7280]">
              {description}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E7E9EC] text-[#9AA0A8] hover:bg-[#F4F6F8]"
          >
            <IconX size={16} stroke={2.2} />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-[18px] py-4">
          <Select
            label="Reason"
            placeholder="Select a reason"
            data={config.reasons}
            radius="md"
            classNames={{
              label: 'mb-1 !text-[11px] !font-bold uppercase !text-[#9AA0A8]',
              input:
                '!min-h-9 !border-[#E7E9EC] !text-[13px] !font-semibold !text-[#1F2733] placeholder:!text-[#B4B9C0]',
              error: '!text-[11px]',
            }}
            comboboxProps={{ withinPortal: false }}
            {...form.getInputProps('reason')}
          />
          {form.values.reason === OTHER_REASON_VALUE && (
            <Textarea
              label="Other reason"
              placeholder="Enter reason"
              autosize
              minRows={3}
              radius="md"
              classNames={{
                label: 'mb-1 !text-[11px] !font-bold uppercase !text-[#9AA0A8]',
                input:
                  '!border-[#E7E9EC] !text-[13px] !font-semibold !text-[#1F2733] placeholder:!text-[#B4B9C0]',
                error: '!text-[11px]',
              }}
              {...form.getInputProps('otherReason')}
            />
          )}
          <Textarea
            label={config.notesLabel}
            placeholder={config.notesPlaceholder}
            autosize
            minRows={4}
            radius="md"
            classNames={{
              label: 'mb-1 !text-[11px] !font-bold uppercase !text-[#9AA0A8]',
              input:
                '!border-[#E7E9EC] !text-[13px] !font-semibold !text-[#1F2733] placeholder:!text-[#B4B9C0]',
              error: '!text-[11px]',
            }}
            {...form.getInputProps('notes')}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-[#EEF0F2] bg-[#FAFBFC] px-[18px] py-3">
          <Button
            variant="default"
            type="button"
            onClick={handleClose}
            radius="md"
            className="!h-8 !border-[#E7E9EC] !px-3 !text-[12px] !font-bold !text-[#33383F] hover:!bg-[#F4F6F8]"
          >
            Cancel
          </Button>
          <Button
            color={config.submitColor}
            type="submit"
            loading={loading}
            radius="md"
            className={`!h-8 !px-3 !text-[12px] !font-bold !text-white ${submitClasses}`}
          >
            {config.submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
